import React from 'react';
import { X } from 'lucide-react';
import ShareButton, { type SharingPlatform } from './ShareButton';
import { generateShareMessage, generateShareUrl, generateHashtags } from '../../utils/sharingUtils';
import type { Group } from '../../types/group';
import type { Event } from '../../types/event';

interface ShareMenuProps {
  type: 'group' | 'event';
  data: Group | Event;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const platforms: SharingPlatform[] = ['whatsapp', 'whatsapp-web', 'facebook', 'reddit', 'linkedin', 'telegram', 'instagram', 'copy'];

const ShareMenu: React.FC<ShareMenuProps> = ({
  type,
  data,
  isOpen,
  onClose,
  className = ''
}) => {
  if (!isOpen) return null;

  // Generate sharing data
  const itemName = type === 'group' ? (data as Group).name : (data as Event).title;
  const eventName = type === 'group' ? (data as Group).event?.name || itemName : itemName;
  const shareMessage = generateShareMessage(type, eventName);
  const shareUrl = generateShareUrl(type, type === 'group' ? (data as Group).id : (data as Event).slug || (data as Event).id.toString());
  const hashtags = generateHashtags(type, itemName);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <div
      className="fixed inset-0 bg-canvas/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div
        className={`
          bg-surface
          border
          border-white/[.09]
          rounded-card
          shadow-panel
          max-w-md
          w-full
          p-6
          transform
          transition-all
          duration-200
          scale-100
          ${className}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-fg-bright">
            Deel deze {type === 'group' ? 'groep' : 'evenement'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-fg-muted hover:text-fg-bright rounded-xl hover:bg-white/[.06] transition-colors"
            aria-label="Sluiten"
          >
            <X size={20} />
          </button>
        </div>

        {/* Preview */}
        <div className="mb-6 p-4 sf-item">
          <h3 className="font-semibold text-fg-bright mb-1">{itemName}</h3>
          {type === 'group' && (data as Group).event && (
            <p className="text-sm text-fg-muted mb-2">
              Evenement: {(data as Group).event?.name}
            </p>
          )}
          <p className="text-sm text-fg-body break-words">
            {shareMessage}
          </p>
        </div>

        {/* Share Buttons Grid */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {platforms.map((platform) => (
            <div key={platform} className="flex flex-col items-center space-y-2">
              <ShareButton
                platform={platform}
                url={shareUrl}
                title={shareMessage}
                description={type === 'group' ? (data as Group).description || undefined : (data as Event).description || undefined}
                hashtags={hashtags}
                size="lg"
              />
              <span className="text-xs text-fg-meta text-center capitalize">
                {platform === 'copy' ? 'Kopiëren' :
                 platform === 'whatsapp-web' ? 'WhatsApp Web' :
                 platform}
              </span>
            </div>
          ))}
        </div>

        {/* URL Display */}
        <div className="border-t border-white/[.08] pt-4">
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-white/[.04] border border-white/[.08] rounded-item px-3 py-2 text-sm text-fg-muted font-mono break-all">
              {shareUrl}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareMenu;